/**
 * A deliberately tiny document model for `UIRichTextEditor`.
 *
 * Toolbar commands run as pure transforms over this model rather than as
 * `Range`/`execCommand` surgery on live DOM: browsers disagree wildly about the
 * markup contenteditable produces (`<b>` vs `<span style="font-weight:700">`,
 * `<div>` vs `<p>`), so we parse whatever they hand us into one canonical shape,
 * transform that, and re-render. Everything here is DOM-parsing but
 * selection-free, which makes it unit-testable in jsdom.
 *
 * Offsets are expressed against `getDocText(doc)` — the block texts joined with
 * `\n` — so a caret position survives the parse → transform → serialize round
 * trip.
 */

export type RichTextAlign = 'left' | 'center' | 'right' | 'justify'
export type RichTextBlockType = 'p' | 'h3'

export interface RichTextMarks {
  isBold?: boolean
  isItalic?: boolean
  href?: string
}

export interface RichTextSpan {
  text: string
  marks: RichTextMarks
}

export interface RichTextBlock {
  type: RichTextBlockType
  align: RichTextAlign
  spans: RichTextSpan[]
}

export type RichTextDoc = RichTextBlock[]

const ALIGNS = new Set<string>(['left', 'center', 'right', 'justify'])
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6'])
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'BLOCKQUOTE',
  'SECTION',
  'ARTICLE',
  ...HEADING_TAGS,
])
/** Never contributes text — a paste of these should vanish, not become copy. */
const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEMPLATE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'HEAD',
])

const emptyBlock = (): RichTextBlock => ({
  type: 'p',
  align: 'left',
  spans: [],
})

export const emptyDoc = (): RichTextDoc => [emptyBlock()]

const sameMarks = (a: RichTextMarks, b: RichTextMarks): boolean =>
  !!a.isBold === !!b.isBold &&
  !!a.isItalic === !!b.isItalic &&
  (a.href ?? '') === (b.href ?? '')

/**
 * Only http(s)/mailto survive, and a bare `example.com` is promoted rather than
 * left as a relative path that would resolve against the app's own origin.
 */
export const normalizeHref = (raw: string | null | undefined): string => {
  const href = (raw ?? '').trim()
  if (!href) return ''
  if (/^(https?:|mailto:)/i.test(href)) return href
  // Anything that declares some *other* scheme (javascript:, data:, vbscript:)
  // is dropped outright rather than prefixed into a plausible-looking URL.
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return ''
  return `https://${href}`
}

// -- parse -------------------------------------------------------------------

const collapseWhitespace = (text: string): string =>
  // JS `\s` already covers the non-breaking spaces contenteditable inserts to
  // protect runs of spaces, so they normalise to a plain space here.
  text.replace(/\s+/g, ' ')

const readAlign = (element: HTMLElement): RichTextAlign | null => {
  const align = element.style?.textAlign
  return align && ALIGNS.has(align) ? (align as RichTextAlign) : null
}

const isBoldStyle = (element: HTMLElement): boolean => {
  const weight = element.style?.fontWeight
  return weight === 'bold' || Number(weight) >= 600
}

const isItalicStyle = (element: HTMLElement): boolean =>
  element.style?.fontStyle === 'italic'

/**
 * Whether anything after this node still contributes copy to its block.
 *
 * Browsers park a filler `<br>` inside an otherwise empty block so it stays
 * clickable (`<p><br></p>`). That `<br>` is layout, not a line the author typed:
 * treating it as a block split would add one empty block per parse, so a blank
 * line would double on every round trip.
 */
const hasContentAfter = (node: Node): boolean => {
  const contributes = (sibling: Node): boolean =>
    (sibling.nodeType === Node.TEXT_NODE && !!(sibling as Text).data.trim()) ||
    (sibling.nodeType === Node.ELEMENT_NODE &&
      !SKIP_TAGS.has((sibling as HTMLElement).tagName))

  // Climb out of inline wrappers as well as scanning siblings: contenteditable
  // readily emits `<strong>text<br></strong>rest`, and looking only inside the
  // `<strong>` would call that break filler and delete the author's line.
  let current: Node | null = node
  while (current) {
    for (
      let sibling = current.nextSibling;
      sibling;
      sibling = sibling.nextSibling
    ) {
      if (contributes(sibling)) return true
    }

    const parent = current.parentNode
    if (!parent || parent.nodeType !== Node.ELEMENT_NODE) return false
    // Stop at the block: copy in a *later* block is a different line already.
    if (BLOCK_TAGS.has((parent as HTMLElement).tagName)) return false
    current = parent
  }
  return false
}

/**
 * Accepts the *wide* markup contenteditable and paste produce — `<b>`, `<i>`,
 * `<div>`, `<font>`, inline styles — and narrows it to the canonical model.
 * (`sanitizeRichText` guards the serialized output; this guards the shape.)
 */
export const parseRichText = (html: string): RichTextDoc => {
  // `<template>` and not a detached `<div>`: a div belongs to the live
  // document, so assigning `innerHTML` builds *live* nodes — an `<img>` in the
  // source starts fetching and fires its `onerror` handler, executing the
  // markup this parser was handed. A template's content is an inert document,
  // where nothing loads and no handler runs. Callers reach here with markup
  // that has not been sanitised yet (paste, and stored announcement copy read
  // for its heading), so parsing has to be safe on its own.
  const template = document.createElement('template')
  template.innerHTML = html ?? ''
  const host = template.content

  const blocks: RichTextBlock[] = []
  let current: RichTextBlock | null = null

  const startBlock = (type: RichTextBlockType, align: RichTextAlign) => {
    current = { type, align, spans: [] }
    blocks.push(current)
  }

  const pushText = (text: string, marks: RichTextMarks) => {
    if (!text) return
    // Whitespace between two block elements is layout, not copy — without this
    // every newline in the source HTML would open a stray paragraph.
    if (!text.trim() && (!current || current.spans.length === 0)) return
    if (!current) startBlock('p', 'left')

    const block = current as RichTextBlock
    const last = block.spans[block.spans.length - 1]
    if (last && sameMarks(last.marks, marks)) last.text += text
    else block.spans.push({ text, marks: { ...marks } })
  }

  const walk = (
    node: Node,
    marks: RichTextMarks,
    inheritedAlign: RichTextAlign | null,
  ) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        pushText(collapseWhitespace((child as Text).data), marks)
        return
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return

      const element = child as HTMLElement
      const tag = element.tagName

      if (SKIP_TAGS.has(tag)) return

      if (tag === 'BR') {
        // A `<br>` closing its block is the browser's filler, not a line break
        // the author typed — see `hasContentAfter`.
        if (!hasContentAfter(element)) return
        // Shift+Enter and Enter both mean "new paragraph" in a schema with no
        // hard-break node; keeping the current block's type/align makes that
        // read as a continuation rather than a reset.
        startBlock(
          current?.type ?? 'p',
          current?.align ?? inheritedAlign ?? 'left',
        )
        return
      }

      if (BLOCK_TAGS.has(tag)) {
        const align = readAlign(element) ?? inheritedAlign ?? 'left'
        startBlock(HEADING_TAGS.has(tag) ? 'h3' : 'p', align)
        walk(element, marks, align)
        // Anything following a block starts a fresh one rather than appending
        // to the block that just closed.
        current = null
        return
      }

      const nextMarks: RichTextMarks = { ...marks }
      if (tag === 'STRONG' || tag === 'B' || isBoldStyle(element)) {
        nextMarks.isBold = true
      }
      if (tag === 'EM' || tag === 'I' || isItalicStyle(element)) {
        nextMarks.isItalic = true
      }
      if (tag === 'A') {
        const href = normalizeHref(element.getAttribute('href'))
        if (href) nextMarks.href = href
      }
      walk(element, nextMarks, inheritedAlign)
    })
  }

  walk(host, {}, null)

  const trimmed = blocks.map((block) => {
    const spans = block.spans.filter((span) => span.text.length > 0)
    if (spans.length) {
      spans[0] = { ...spans[0], text: spans[0].text.replace(/^ +/, '') }
      const lastIndex = spans.length - 1
      spans[lastIndex] = {
        ...spans[lastIndex],
        text: spans[lastIndex].text.replace(/ +$/, ''),
      }
    }
    return { ...block, spans: spans.filter((span) => span.text.length > 0) }
  })

  // Empty blocks are kept: a blank line the author typed is content, and
  // dropping it here silently deleted it from the document on every re-render
  // (blur, or any toolbar command). Trailing filler is already excluded by the
  // `<br>` rule above.
  return trimmed.length ? trimmed : emptyDoc()
}

// -- serialize ---------------------------------------------------------------

const escapeText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const escapeAttribute = (value: string): string =>
  escapeText(value).replace(/"/g, '&quot;')

/** Canonical nesting: link outermost, then bold, then italic. */
const spanToHtml = (span: RichTextSpan): string => {
  let html = escapeText(span.text)
  if (span.marks.isItalic) html = `<em>${html}</em>`
  if (span.marks.isBold) html = `<strong>${html}</strong>`
  if (span.marks.href) {
    html = `<a href="${escapeAttribute(
      span.marks.href,
    )}" target="_blank" rel="noopener noreferrer">${html}</a>`
  }
  return html
}

export const serializeRichText = (doc: RichTextDoc): string =>
  doc
    .map((block) => {
      const inner = block.spans.map(spanToHtml).join('')
      const style =
        block.align === 'left' ? '' : ` style="text-align: ${block.align}"`
      return `<${block.type}${style}>${inner || '<br>'}</${block.type}>`
    })
    .join('')

/** Round-trips arbitrary markup through the model to its canonical form. */
export const normalizeRichText = (html: string): string =>
  serializeRichText(parseRichText(html))

// -- offsets -----------------------------------------------------------------

export const getBlockText = (block: RichTextBlock): string =>
  block.spans.map((span) => span.text).join('')

/** Blocks joined by `\n`, matching the offset space commands operate in. */
export const getDocText = (doc: RichTextDoc): string =>
  doc.map(getBlockText).join('\n')

export interface BlockRange {
  start: number
  end: number
}

export const getBlockRanges = (doc: RichTextDoc): BlockRange[] => {
  let cursor = 0
  return doc.map((block) => {
    const start = cursor
    const end = start + getBlockText(block).length
    cursor = end + 1 // the joining "\n"
    return { start, end }
  })
}

// -- commands ----------------------------------------------------------------

interface MarkedChar {
  char: string
  marks: RichTextMarks
}

/**
 * Per-character expansion keeps span splitting obviously correct at the cost of
 * an allocation per character. Announcement bodies are a few hundred characters,
 * so the trade is firmly on the side of "no off-by-one in a mark boundary".
 */
const blockToChars = (block: RichTextBlock): MarkedChar[] =>
  block.spans.flatMap((span) =>
    // Split by UTF-16 code unit, not code point. `getBlockText().length`, the
    // selection layer (`node.data.length`) and the paste caret all count code
    // units, so iterating code points here would shift every offset after an
    // emoji by one and land marks in the wrong place.
    span.text.split('').map((char) => ({ char, marks: span.marks })),
  )

const charsToSpans = (chars: MarkedChar[]): RichTextSpan[] => {
  const spans: RichTextSpan[] = []
  chars.forEach(({ char, marks }) => {
    const last = spans[spans.length - 1]
    if (last && sameMarks(last.marks, marks)) last.text += char
    else spans.push({ text: char, marks: { ...marks } })
  })
  return spans
}

const overlapInBlock = (
  range: BlockRange,
  from: number,
  to: number,
): [number, number] | null => {
  const start = Math.max(from, range.start)
  const end = Math.min(to, range.end)
  if (end <= start) return null
  return [start - range.start, end - range.start]
}

const mapMarksInRange = (
  doc: RichTextDoc,
  from: number,
  to: number,
  transform: (marks: RichTextMarks) => RichTextMarks,
): RichTextDoc => {
  if (to <= from) return doc
  const ranges = getBlockRanges(doc)

  return doc.map((block, index) => {
    const overlap = overlapInBlock(ranges[index], from, to)
    if (!overlap) return block

    const [start, end] = overlap
    const chars = blockToChars(block).map((entry, charIndex) =>
      charIndex >= start && charIndex < end
        ? { ...entry, marks: transform(entry.marks) }
        : entry,
    )
    return { ...block, spans: charsToSpans(chars) }
  })
}

/** Blocks touched by the selection; a collapsed caret still resolves to one. */
const blockIndexesInRange = (
  doc: RichTextDoc,
  from: number,
  to: number,
): number[] => {
  const ranges = getBlockRanges(doc)
  const touched = ranges.reduce<number[]>((acc, range, index) => {
    if (from <= range.end && to >= range.start) acc.push(index)
    return acc
  }, [])
  return touched.length ? touched : [0]
}

export const getCharsInRange = (
  doc: RichTextDoc,
  from: number,
  to: number,
): MarkedChar[] => {
  const ranges = getBlockRanges(doc)
  return doc.flatMap((block, index) => {
    const overlap = overlapInBlock(ranges[index], from, to)
    if (!overlap) return []
    return blockToChars(block).slice(overlap[0], overlap[1])
  })
}

export const setBlockType = (
  doc: RichTextDoc,
  from: number,
  to: number,
  type: RichTextBlockType,
): RichTextDoc => {
  const targets = new Set(blockIndexesInRange(doc, from, to))
  return doc.map((block, index) =>
    targets.has(index) ? { ...block, type } : block,
  )
}

export const setBlockAlign = (
  doc: RichTextDoc,
  from: number,
  to: number,
  align: RichTextAlign,
): RichTextDoc => {
  const targets = new Set(blockIndexesInRange(doc, from, to))
  return doc.map((block, index) =>
    targets.has(index) ? { ...block, align } : block,
  )
}

/**
 * Toggles off only when the *entire* selection already carries the mark —
 * partially-bold text bolds the rest, matching every editor users know.
 */
export const toggleMark = (
  doc: RichTextDoc,
  from: number,
  to: number,
  mark: 'isBold' | 'isItalic',
): RichTextDoc => {
  if (to <= from) return doc
  const chars = getCharsInRange(doc, from, to)
  const shouldSet = !chars.every((entry) => !!entry.marks[mark])

  return mapMarksInRange(doc, from, to, (marks) => {
    const next = { ...marks }
    if (shouldSet) next[mark] = true
    else delete next[mark]
    return next
  })
}

/**
 * With a collapsed caret inside a link, the whole link run is the target —
 * otherwise "edit this link" would silently do nothing.
 */
export const expandToLinkRange = (
  doc: RichTextDoc,
  from: number,
  to: number,
): [number, number] => {
  if (to > from) return [from, to]

  // `flattenDoc` carries one entry per block joiner, so its indexes are the same
  // offsets the caller measured in. A plain character list omits those joiners
  // and drifts by one per preceding block — which linked the wrong characters in
  // any document with more than one block.
  const entries = flattenDoc(doc)
  const hrefAt = (index: number): string | undefined => {
    const entry = entries[index]
    return entry && !entry.isBreak ? entry.marks.href : undefined
  }

  const href = hrefAt(from) ?? hrefAt(from - 1)
  if (!href) return [from, to]

  let start = from
  let end = from
  while (start > 0 && hrefAt(start - 1) === href) start -= 1
  while (end < entries.length && hrefAt(end) === href) end += 1
  return [start, end]
}

export const setLink = (
  doc: RichTextDoc,
  from: number,
  to: number,
  href: string | null,
): RichTextDoc => {
  const [start, end] = expandToLinkRange(doc, from, to)
  if (end <= start) return doc

  const normalized = href === null ? '' : normalizeHref(href)
  return mapMarksInRange(doc, start, end, (marks) => {
    const next = { ...marks }
    if (normalized) next.href = normalized
    else delete next.href
    return next
  })
}

/**
 * Flat view of the document in offset space: one entry per character, plus one
 * `isBreak` entry per block boundary (the `\n` of `getDocText`). Splicing here
 * keeps insert/delete honest across block boundaries — deleting a break merges
 * two blocks and the survivor keeps the *first* block's attributes, which is
 * what carrying them on the following break gives us for free.
 */
type FlatEntry =
  | { isBreak: false; char: string; marks: RichTextMarks }
  | { isBreak: true; type: RichTextBlockType; align: RichTextAlign }

const flattenDoc = (doc: RichTextDoc): FlatEntry[] =>
  doc.flatMap((block, index) => {
    const chars: FlatEntry[] = blockToChars(block).map(({ char, marks }) => ({
      isBreak: false,
      char,
      marks,
    }))
    if (index === 0) return chars
    // The break carries the attributes of the block it opens.
    return [
      { isBreak: true, type: block.type, align: block.align } as FlatEntry,
      ...chars,
    ]
  })

const unflatten = (
  entries: FlatEntry[],
  firstBlock: Pick<RichTextBlock, 'type' | 'align'>,
): RichTextDoc => {
  const blocks: RichTextBlock[] = [{ ...firstBlock, spans: [] }]
  const chars: MarkedChar[][] = [[]]

  entries.forEach((entry) => {
    if (entry.isBreak) {
      blocks.push({ type: entry.type, align: entry.align, spans: [] })
      chars.push([])
      return
    }
    chars[chars.length - 1].push({ char: entry.char, marks: entry.marks })
  })

  return blocks.map((block, index) => ({
    ...block,
    spans: charsToSpans(chars[index]),
  }))
}

/**
 * Replaces `[from, to)` with plain `text`. Newlines in `text` become block
 * splits, and inserted characters inherit the marks of the character they
 * follow — so typing at the end of a bold run stays bold.
 */
export const replaceRange = (
  doc: RichTextDoc,
  from: number,
  to: number,
  text: string,
): RichTextDoc => {
  const entries = flattenDoc(doc)
  const start = Math.max(0, Math.min(from, entries.length))
  const end = Math.max(start, Math.min(to, entries.length))

  const before = entries[start - 1]
  const inheritedMarks =
    before && !before.isBreak ? before.marks : ({} as RichTextMarks)
  // The block the caret sits in, not the first one in the document: pasting two
  // lines into the body of a notification that opens with a heading must not
  // turn the second line into a heading.
  const current =
    doc[blockIndexesInRange(doc, from, from)[0]] ?? doc[0] ?? emptyBlock()
  const blockAttrs = { type: current.type, align: current.align }

  const inserted: FlatEntry[] = text
    .split('')
    .flatMap((char) =>
      char === '\n'
        ? [{ isBreak: true, ...blockAttrs } as FlatEntry]
        : [{ isBreak: false, char, marks: { ...inheritedMarks } } as FlatEntry],
    )

  const next = [...entries.slice(0, start), ...inserted, ...entries.slice(end)]

  const rebuilt = unflatten(next, {
    type: doc[0]?.type ?? 'p',
    align: doc[0]?.align ?? 'left',
  })
  return rebuilt.length ? rebuilt : emptyDoc()
}
